import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Building2, Folder } from 'lucide-react'
import { getAreaBySlug, getAreaTree, areaKeys } from '@/lib/api/areas'
import { findAreaLocation, isCardsPage, countDescendantAreas } from '@/lib/areaTree'
import { AreaCardsGrid } from '@/components/ged/AreaCardsGrid'
import { AreaDirectFiles } from '@/components/ged/AreaDirectFiles'
import { UploadTriggerButton } from '@/components/ged/UploadTriggerButton'
import { AreaContentPage } from '@/pages/AreaContentPage'

export function AreaPage() {
  const { slug } = useParams<{ slug: string }>()

  const { data: tree = [], isLoading: isTreeLoading } = useQuery({ queryKey: areaKeys.tree(), queryFn: getAreaTree })
  const { data: area, isLoading } = useQuery({
    queryKey: areaKeys.detailBySlug(slug ?? ''),
    queryFn: () => getAreaBySlug(slug!),
    enabled: !!slug,
  })

  if (isLoading || isTreeLoading || !area) {
    return (
      <div style={{ padding: '28px 32px' }}>
        <p style={{ color: 'var(--ev-fg-muted)', fontSize: 'var(--fs-body)' }}>Carregando…</p>
      </div>
    )
  }

  const location = findAreaLocation(tree, area.id)
  const depth = location?.depth ?? 1
  const children = location?.node.children ?? []
  const hasChildren = location?.node.hasChildren ?? false
  const showCards = isCardsPage(depth, hasChildren) && children.length > 0
  const breadcrumbChain = location ? [...location.ancestors, location.node] : []
  const Icon = depth === 1 ? Building2 : Folder
  const subAreaCount = location ? countDescendantAreas(location.node) : 0

  return (
    <div style={{ padding: '28px 32px', maxWidth: 1200 }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap', fontSize: 'var(--fs-small)', color: 'var(--ev-fg-muted)', marginBottom: 20 }}>
        <Link to="/" style={{ color: 'var(--ev-fg-muted)', textDecoration: 'none' }}>Índice</Link>
        {breadcrumbChain.map((node, i) => (
          <span key={node.id} style={{ display: 'contents' }}>
            <span style={{ color: 'var(--ev-fg-faint)', fontSize: 'var(--fs-micro)' }}>›</span>
            {i === breadcrumbChain.length - 1 ? (
              <span style={{ color: 'var(--ev-fg)', fontWeight: 500 }}>{node.name}</span>
            ) : (
              <Link to={`/areas/${node.slug}`} style={{ color: 'var(--ev-fg-muted)', textDecoration: 'none' }}>
                {node.name}
              </Link>
            )}
          </span>
        ))}
      </div>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: 8, background: 'var(--ev-accent-soft)', color: 'var(--ev-accent)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
              <Icon size={20} />
            </div>
            <h1 style={{ color: 'var(--ev-fg-strong)', margin: 0 }}>{area.name}</h1>
          </div>
          <UploadTriggerButton areaId={area.id} areaName={area.name} />
        </div>
        {area.description && (
          <p style={{ color: 'var(--ev-fg-muted)', fontSize: 'var(--fs-body)', maxWidth: 720, margin: '0 0 10px' }}>{area.description}</p>
        )}
        <div style={{ fontSize: 'var(--fs-small)', color: 'var(--ev-fg-muted)' }}>
          {subAreaCount > 0 && `${subAreaCount} subáreas · `}{area.documentCount} arquivos
        </div>
      </div>

      {showCards ? (
        <>
          <AreaCardsGrid nodes={children} />
          {/* Arquivos que pertencem a este nó e não a nenhuma subárea — ex. um regulamento
              geral. Não renderiza nada quando a área não tem arquivos próprios. */}
          <AreaDirectFiles areaId={area.id} />
        </>
      ) : (
        <AreaContentPage area={area} folders={children} />
      )}
    </div>
  )
}
